import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { ALL_TOOLS } from '../lib/toolsCatalog';
import { SIMULATOR_CATALOG } from '../lib/simulationCatalog';
import { useArticleSections } from '../lib/articlesHierarchy';
import { useHowItWorksTopics } from '../lib/howItWorksHierarchy';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import CompactCount from '../components/admin/CompactCount';
import { ArrowLeft, ArrowUp, ArrowDown, ArrowUpDown, Search, BarChart3 } from 'lucide-react';
import { format } from 'date-fns';

// Same 4 sections + order as the dashboard's VisitStatsCards.
const SECTIONS = [
  { key: 'tool', label: 'Tools' },
  { key: 'simulation', label: 'Simulations' },
  { key: 'article', label: 'Articles' },
  { key: 'how_it_works', label: 'How It Works' },
];

const SHOW_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'visited', label: 'Visited' },
  { value: 'never', label: 'Never visited' },
];

// Same fallback label as the dashboard cards, for visited paths with no catalog entry.
const pathLabel = (path) => {
  const last = path.split('/').filter(Boolean).pop() || path;
  const words = decodeURIComponent(last).replace(/[-_]+/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return format(new Date(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
};

const SortableHead = ({ label, column, sort, order, onSort, className = '' }) => {
  const active = sort === column;
  const Icon = !active ? ArrowUpDown : order === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className} aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 whitespace-nowrap hover:text-foreground"
        data-testid={`sort-${column}`}
      >
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? 'text-autoxium-blue' : 'opacity-50'}`} />
      </button>
    </TableHead>
  );
};

// Full per-item visit list for one section. The backend only knows items that have
// been visited, so every catalog item (the same catalogs the public Trending sections
// rank) is merged in with 0, and visited paths missing from the catalog (removed,
// renamed or test pages) are kept and flagged "Not listed".
const AdminVisitsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const section = SECTIONS.some((s) => s.key === searchParams.get('section'))
    ? searchParams.get('section')
    : 'tool';

  const { articles, loading: articlesLoading } = useArticleSections();
  const { topics, loading: topicsLoading } = useHowItWorksTopics();

  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [show, setShow] = useState('all');
  const [sort, setSort] = useState('count');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    let cancelled = false;
    setStats(null);
    setError(false);
    adminApi.getSectionVisitStats(section)
      .then((data) => { if (!cancelled) setStats(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [section]);

  const catalog = useMemo(() => {
    if (section === 'tool') return ALL_TOOLS.map((t) => ({ id: t.to, title: t.title, group: t.category }));
    if (section === 'simulation') return SIMULATOR_CATALOG.map((s) => ({ id: s.to, title: s.title, group: null }));
    if (section === 'article') return articles.map((a) => ({ id: a.path, title: a.title, group: a.category || null }));
    return topics.map((t) => ({
      id: `/how-it-works/${t.topic_id}`,
      title: t.title,
      group: t.subcategory || t.category || null,
    }));
  }, [section, articles, topics]);

  const catalogLoading = (section === 'article' && articlesLoading) || (section === 'how_it_works' && topicsLoading);

  const rows = useMemo(() => {
    if (!stats) return [];
    const byId = new Map(stats.items.map((i) => [i.item_id, i]));
    const merged = catalog.map((c) => {
      const s = byId.get(c.id);
      byId.delete(c.id);
      return {
        ...c,
        listed: true,
        count: s?.count || 0,
        count_30d: s?.count_30d || 0,
        last_visited_at: s?.last_visited_at || null,
      };
    });
    byId.forEach((s) => merged.push({
      id: s.item_id,
      title: pathLabel(s.item_id),
      group: null,
      listed: false,
      count: s.count,
      count_30d: s.count_30d,
      last_visited_at: s.last_visited_at,
    }));
    return merged;
  }, [stats, catalog]);

  const visitedCount = rows.filter((r) => r.count > 0).length;

  const visibleRows = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (show === 'visited' && r.count === 0) return false;
      if (show === 'never' && r.count > 0) return false;
      if (!q) return true;
      return r.title.toLowerCase().includes(q) || r.id.toLowerCase().includes(q)
        || (r.group || '').toLowerCase().includes(q);
    });
    const dir = order === 'asc' ? 1 : -1;
    const value = (r) => (sort === 'title' ? r.title.toLowerCase()
      : sort === 'last_visited_at' ? (r.last_visited_at || '') : r[sort]);
    return [...filtered].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return a.title.localeCompare(b.title);
    });
  }, [rows, searchInput, show, sort, order]);

  const handleSort = (column) => {
    if (sort === column) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(column);
      setOrder(column === 'title' ? 'asc' : 'desc');
    }
  };

  const changeSection = (value) => {
    setSearchParams({ section: value }, { replace: true });
    setSearchInput('');
    setShow('all');
  };

  const loading = (!stats && !error) || catalogLoading;

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-visits-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon" data-testid="back-to-admin-btn" aria-label="Back to admin">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="font-chivo text-2xl font-black uppercase tracking-tight">Content Visits</h1>
            <p className="text-muted-foreground text-sm">Every page in each section, including ones nobody has opened yet</p>
          </div>
        </div>

        <Tabs value={section} onValueChange={changeSection} className="mb-4">
          <div className="overflow-x-auto -mx-4 px-4">
            <TabsList className="w-max">
              {SECTIONS.map(({ key, label }) => (
                <TabsTrigger key={key} value={key} data-testid={`section-tab-${key}`}>{label}</TabsTrigger>
              ))}
            </TabsList>
          </div>
        </Tabs>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="relative w-full sm:max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search title, path or category"
              maxLength={100}
              className="pl-9"
              aria-label="Search items"
              data-testid="visits-search-input"
            />
          </div>
          <div className="flex gap-1" role="group" aria-label="Filter by visits">
            {SHOW_FILTERS.map(({ value, label }) => (
              <Button
                key={value}
                variant={show === value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShow(value)}
                data-testid={`visits-filter-${value}`}
              >
                {label}
              </Button>
            ))}
          </div>
          {!loading && !error && (
            <span className="text-sm text-muted-foreground sm:ml-auto" data-testid="visits-summary">
              {rows.length} items · {visitedCount} visited · <CompactCount value={stats.total} /> visits
            </span>
          )}
        </div>

        <Card className="border-2">
          <CardContent className="p-0">
            {error ? (
              <p className="p-6 text-sm text-destructive">Could not load visit stats.</p>
            ) : loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : visibleRows.length === 0 ? (
              <div className="py-16 text-center" data-testid="no-visits-message">
                <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No items match.</p>
              </div>
            ) : (
              <Table data-testid="visits-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <SortableHead label="Item" column="title" sort={sort} order={order} onSort={handleSort} />
                    <SortableHead label="Last 30 days" column="count_30d" sort={sort} order={order} onSort={handleSort} className="hidden sm:table-cell" />
                    <SortableHead label="All time" column="count" sort={sort} order={order} onSort={handleSort} />
                    <SortableHead label="Last visited" column="last_visited_at" sort={sort} order={order} onSort={handleSort} className="hidden md:table-cell" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map((r, i) => (
                    <TableRow key={r.id} data-testid={`visit-row-${r.id.replace(/\W+/g, '-')}`}>
                      <TableCell className="text-xs font-mono text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="max-w-[210px] sm:max-w-[420px]">
                        <div className="flex flex-wrap items-center gap-2 min-w-0">
                          <Link to={r.id} className="font-medium hover:text-autoxium-blue truncate max-w-full">
                            {r.title}
                          </Link>
                          {!r.listed && (
                            <Badge
                              variant="outline"
                              className="whitespace-nowrap bg-amber-500/15 text-amber-600 border-amber-500/30"
                              title="Visited, but no longer in the site catalog (removed, renamed or a test page)"
                            >
                              Not listed
                            </Badge>
                          )}
                        </div>
                        <span className="block text-xs text-muted-foreground truncate">
                          {r.group ? `${r.group} · ` : ''}{r.id}
                        </span>
                        {/* Hidden columns move under the item on narrow screens. */}
                        <span className="sm:hidden block text-xs text-muted-foreground">
                          30 days: <CompactCount value={r.count_30d} className="font-mono" />
                          {r.last_visited_at && <> · last {formatDate(r.last_visited_at)}</>}
                        </span>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell font-mono text-sm">
                        <CompactCount value={r.count_30d} />
                      </TableCell>
                      <TableCell className={`font-mono text-sm ${r.count === 0 ? 'text-muted-foreground' : 'font-semibold'}`}>
                        <CompactCount value={r.count} />
                      </TableCell>
                      <TableCell className="hidden md:table-cell whitespace-nowrap text-sm text-muted-foreground">
                        {formatDate(r.last_visited_at) || 'Never'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminVisitsPage;
